import { getCloudinaryId } from '@masterplandev/utils'

import { env } from '@/env'

export type CloudflareImageOptions = {
  /** Whether to preserve animation frames (default: true) */
  anim?: boolean
  /** Background color for transparent images or when using fit=pad */
  background?: string
  /** Blur radius between 1 (slight) and 250 (maximum) */
  blur?: number
  /** Brightness factor (1.0 = no change, 0.5 = half brightness, 2.0 = twice as bright) */
  brightness?: number
  /** Compression mode - 'fast' for quickest compression at cost of quality */
  compression?: 'fast'
  /** Contrast factor (1.0 = no change, 0.5 = low contrast, 2.0 = high contrast) */
  contrast?: number
  /** Device Pixel Ratio multiplier for width/height */
  dpr?: number
  /** How the image should be resized to fit the dimensions */
  fit?: 'cover' | 'contain' | 'crop' | 'scale-down' | 'pad'
  /** Output format (e.g. 'auto', 'webp', 'avif', 'jpeg', 'png') */
  format?: string
  /** Gravity for positioning (e.g. 'auto', 'left', 'right', 'top', 'bottom') */
  gravity?: 'auto' | 'left' | 'right' | 'top' | 'bottom'
  /** Height of the image in pixels */
  height?: number
  /** Whether to preserve metadata */
  metadata?: 'keep' | 'none'
  /** Whether to redirect to source on error */
  onerror?: 'redirect'
  /** Quality level (1-100) or perceptual quality level */
  quality?: number | 'high' | 'medium-high' | 'medium-low' | 'low'
  /** Rotation in degrees (90, 180, or 270) */
  rotate?: number
  /** Saturation factor (1.0 = no change, 0.5 = half saturation, 2.0 = twice as saturated, 0 = grayscale) */
  saturation?: number
  /** Sharpening strength between 0 (none) and 10 (maximum) */
  sharpen?: number
  /** Quality level for slow connections */
  slowConnectionQuality?: number | 'high' | 'medium-high' | 'medium-low' | 'low'
  /** Trim pixels from edges (format: "top;right;bottom;left") */
  trim?: string
  /** Width of the image in pixels */
  width?: number
}

/**
 * Converts image options object to Cloudflare transformation string
 *
 * @param options - Options object from CloudinaryElementProps
 * @returns {string} Cloudflare transformation string (e.g. "w=426,h=368,fit=cover,g=auto,f=auto")
 * @example
 * const options = {
 *   width: 426,
 *   height: 368,
 *   fit: 'cover',
 *   gravity: 'auto',
 *   format: 'auto'
 * }
 * const result = optionsToTransformationString(options)
 * // Returns: "w=426,h=368,fit=cover,g=auto,f=auto"
 */
export function optionsToTransformationString(
  options: CloudflareImageOptions,
): string {
  const transformations: string[] = []

  // Map of option keys to their Cloudflare parameter names
  const paramMap: Record<string, string> = {
    width: 'w',
    height: 'h',
    fit: 'fit',
    dpr: 'dpr',
    background: 'background',
    blur: 'blur',
    brightness: 'brightness',
    contrast: 'contrast',
    saturation: 'saturation',
    sharpen: 'sharpen',
    rotate: 'rotate',
    anim: 'anim',
    compression: 'compression',
    quality: 'q',
    slowConnectionQuality: 'scq',
    trim: 'trim',
    gravity: 'g',
    format: 'f',
    metadata: 'metadata',
    onerror: 'onerror',
  }

  // Convert each option to its Cloudflare parameter format
  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined && paramMap[key]) {
      const param = paramMap[key]
      transformations.push(`${param}=${value}`)
    }
  })

  return transformations.join(',')
}

/**
 * Removes common image extensions from a string
 *
 * @param str - The string to remove extensions from
 * @returns {string} The string without image extensions
 */
function removeImageExtensions(str: string): string {
  return str.replace(/\.(jpg|jpeg|png|gif|webp|avif)$/i, '')
}

/**
 * Get the Cloudflare image URL
 *
 * @param id - The ID of the image
 * @param options - The options for the image
 * @returns {string} The Cloudflare image URL
 * @example
 * const url = getCloudflareImageUrl('image_id', { width: 426, height: 368, fit: 'cover', gravity: 'auto', format: 'auto' })
 * // Returns: "/cdn-cgi/image/w=426,h=368,fit=cover,g=auto,f=auto/https://res.cloudinary.com/masterplan/image/upload/v1/image_id"
 */
export function getCloudflareImageUrl(
  id: string,
  options: CloudflareImageOptions,
) {
  const optionsWithDefaults: CloudflareImageOptions = {
    format: 'auto',
    ...options,
  }

  const cloudinaryId = removeImageExtensions(getCloudinaryId(id) ?? id)
  const sourceImageUrl = `https://${env.REACT_APP_CLOUDINARY_CNAME || 'res.cloudinary.com'}/${env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload/${cloudinaryId}`

  return `/cdn-cgi/image/${optionsToTransformationString(optionsWithDefaults)}/${sourceImageUrl}`
}
